import { ErrorBoundary } from '@monorepo/shared/componentsV2/ErrorBoundary';
import { RoutesWith404 } from '@monorepo/shared/componentsV2/RoutesWith404';
import { useFeatureFlags } from 'mapistry-shared/api';
import React from 'react';
import { Route } from 'react-router-dom';
import { usePreventNavigation } from '../../shared/hooks/navigation/usePreventNavigation';
import { EditFormCalendarsPage } from './components/EditFormCalendarsPage/EditFormCalendarsPage';
import { EditFormTemplateDetailsPage } from './components/EditFormTemplateDetailsPage/EditFormTemplateDetailsPage';
import { EditFormTemplateFieldsPage } from './components/EditFormTemplateDetailsPage/EditFormTemplateFieldsPage';
import {
  FormTemplateProvider,
  useFormTemplateContext,
} from './components/EditFormTemplatePage/FormTemplateContext';
import { InvalidFormTemplateErrorPage } from './components/FormTemplateEditor/InvalidFormTemplateErrorPage';
import { WizardStep, WizardStepUrlPart } from './components/shared/consts';

function PreventNavigationGuard({
  formWizardBasePath,
}: {
  formWizardBasePath: string;
}) {
  const { isDirty } = useFormTemplateContext();
  usePreventNavigation(isDirty, {
    title: 'Close Editor',
    description:
      'Are you sure you want to close without saving? You may have unsaved changes.',
    allowNavigationWithinSubPagesUrlPath: formWizardBasePath,
  });

  return null;
}

export function FormTemplateWizardRouter({
  isEditMode,
  isUsingOrgSpecificEditor,
  formWizardBasePath,
  hasOrgUpdatePermissions,
}: {
  isEditMode: boolean;
  isUsingOrgSpecificEditor: boolean;
  formWizardBasePath: string;
  hasOrgUpdatePermissions: boolean;
}) {
  const { isAssigningFormCalendarsEnabled } = useFeatureFlags();
  const canEditCalendars =
    isAssigningFormCalendarsEnabled &&
    isUsingOrgSpecificEditor &&
    hasOrgUpdatePermissions &&
    isEditMode;

  return (
    <ErrorBoundary Fallback={InvalidFormTemplateErrorPage}>
      <FormTemplateProvider>
        <PreventNavigationGuard formWizardBasePath={formWizardBasePath} />
        <RoutesWith404>
          <Route
            index
            element={
              <EditFormTemplateDetailsPage
                formWizardBasePath={formWizardBasePath}
                isEditMode={isEditMode}
              />
            }
          />
          <Route
            path={WizardStepUrlPart[WizardStep.FIELDS]}
            element={
              <EditFormTemplateFieldsPage
                formWizardBasePath={formWizardBasePath}
                isEditMode={isEditMode}
              />
            }
          />
          {canEditCalendars && (
            <Route
              path={WizardStepUrlPart[WizardStep.CALENDARS]}
              element={
                <EditFormCalendarsPage
                  formWizardBasePath={formWizardBasePath}
                />
              }
            />
          )}
        </RoutesWith404>
      </FormTemplateProvider>
    </ErrorBoundary>
  );
}
